import type {FC} from 'react';

import {FormControl, RadioGroup, FormControlLabel, Radio} from '@mui/material';
import {Box, Stack, TextField, FormRow} from '@wellstone-solutions/web';
import type {FormType} from '@wellstone-solutions/common';
import type {GroupEvent} from 'api/v2';
import {SelectState} from 'components/SelectState';

type PropsType = {
  form: FormType<GroupEvent>;
};

export const EventDataForm: FC<PropsType> = ({form}) => {
  const inputProps = {sx: {backgroundColor: '#fff'}};
  const isVirtual = form.values.data.eventType === 'Virtual';

  return (
    <>
      <FormControl>
        <RadioGroup
          name="group-event-radio-buttons-group"
          onChange={(event) => {
            form.setFieldValue('data.eventType', event.target.value);
            form.setFieldTouched('data.eventType', true);
          }}
          value={form.values.data.eventType}>
          <Stack direction="row">
            <FormControlLabel
              value="In person"
              control={<Radio />}
              label="In person"
            />
            <FormControlLabel
              value="Virtual"
              control={<Radio />}
              label="Virtual"
            />
          </Stack>
        </RadioGroup>
      </FormControl>
      {/* In person form */}
      <Stack direction="column" sx={{display: isVirtual ? 'none' : 'flex'}}>
        <FormRow>
          <TextField
            InputProps={inputProps}
            formField="data.location.name"
            label="Location name"
            onBlur={() => {
              form.setFieldTouched('data.location.name', true);
            }}
            onChange={form.handleFieldChange('data.location.name')}
            value={form.values.data.location.name}
            sx={{width: {xs: '100%', md: '75%'}}}
          />
        </FormRow>
        <FormRow>
          <TextField
            InputProps={inputProps}
            formField="data.phone"
            label="Phone number"
            onChange={form.handleFieldChange('data.phone')}
            value={form.values.data.phone}
            sx={{width: {xs: '100%', md: '50%'}}}
          />
        </FormRow>
        <FormRow>
          <TextField
            InputProps={inputProps}
            formField="data.location.address1"
            label="Address"
            onBlur={() => {
              form.setFieldTouched('data.location.address1', true);
            }}
            onChange={form.handleFieldChange('data.location.address1')}
            value={form.values.data.location.address1}
          />
        </FormRow>
        <FormRow>
          <TextField
            InputProps={inputProps}
            formField="data.location.address2"
            label="Address 2"
            onChange={form.handleFieldChange('data.location.address2')}
            value={form.values.data.location.address2}
          />
        </FormRow>

        <FormRow>
          <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
            <TextField
              InputProps={{sx: {backgroundColor: '#fff'}}}
              error={Boolean(
                form.touched?.data?.location?.city &&
                Boolean(form.errors?.data?.location?.city),
              )}
              formField="data.location.city"
              label="City"
              onChange={form.handleFieldChange('data.location.city')}
              value={form.values.data.location.city}
              fullWidth
              sx={{
                maxWidth: {
                  xs: '100%',
                  md: '50%',
                },
              }}
              helperText={form.errors?.data?.location?.city}
            />

            <Box sx={{flex: 1, width: '100%'}}>
              <FormControl fullWidth>
                <SelectState
                  onChange={(event) => {
                    form.setFieldValue(
                      'data.location.state',
                      event?.target.value,
                    );
                  }}
                  value={form.values.data.location.state}
                  sx={{
                    backgroundColor: '#fff',
                  }}
                />
              </FormControl>
            </Box>

            <TextField
              InputProps={{
                sx: {backgroundColor: '#fff'},
              }}
              fullWidth
              formField="data.location.zip"
              label="Zip"
              onChange={form.handleFieldChange('data.location.zip')}
              value={form.values.data.location.zip}
              sx={{
                mb: 1,
                width: {
                  xs: '100%',
                  md: '25%',
                },
              }}
            />
          </Stack>
        </FormRow>
      </Stack>
      {/* Virtual form */}
      <Stack direction="column" sx={{display: isVirtual ? 'flex' : 'none'}}>
        <FormRow>
          <TextField
            multiline
            rows={3}
            InputProps={inputProps}
            formField="data.link"
            label="Meeting Link"
            onChange={form.handleFieldChange('data.link')}
            value={form.values.data.link}
            sx={{mb: 1}}
          />
        </FormRow>
      </Stack>
      <FormRow>
        <TextField
          multiline
          rows={2}
          InputProps={inputProps}
          formField="data.notes"
          label="Description of Agenda (Optional)"
          onChange={form.handleFieldChange('data.notes')}
          value={form.values.data.notes}
          sx={{mb: 2}}
        />
      </FormRow>
    </>
  );
};
